import { useState, useEffect } from 'react';
import { Seo, usePageSeo, assetUrl, absUrl } from '../lib/seo';
import { PageSeoContent } from '../components/common/RichContent';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Clock, ChevronRight, ArrowRight, BookOpen, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { blogsAPI } from '../lib/api';

const categories = [
  { key: 'all', label: 'All Articles' },
  { key: 'skin', label: 'Skin Care' },
  { key: 'hair', label: 'Hair Care' },
  { key: 'weight', label: 'Weight Management' },
  { key: 'aesthetic', label: 'Aesthetic' },
  { key: 'general', label: 'General' },
];

export const BlogsPage = () => {
  const seo = usePageSeo();
  const [searchParams, setSearchParams] = useSearchParams();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const activeCategory = searchParams.get('category') || 'all';

  const LIMIT = 12;

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      try {
        const params = {
          limit: LIMIT,
          skip: page * LIMIT,
          is_published: true,
          ...(activeCategory !== 'all' && { category: activeCategory }),
        };
        const [blogsRes, countRes] = await Promise.all([
          blogsAPI.getAll(params),
          blogsAPI.getCount(activeCategory !== 'all' ? activeCategory : undefined),
        ]);
        setBlogs(blogsRes.data);
        setTotalCount(countRes.data.count);
      } catch (error) {
        console.error('Error fetching blogs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, [activeCategory, page]);

  const handleCategoryChange = (category) => {
    setPage(0);
    if (category === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const featuredBlogs = blogs.slice(0, 3);
  const regularBlogs = blogs.slice(3);

  const getCategoryColor = (cat) => {
    const colors = {
      skin: 'bg-amber-100 text-amber-800',
      hair: 'bg-gray-800 text-white',
      weight: 'bg-emerald-100 text-emerald-800',
      aesthetic: 'bg-purple-100 text-purple-800',
      general: 'bg-blue-100 text-blue-800',
    };
    return colors[cat] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-alabaster" data-testid="blogs-page">
      <Seo override={seo} title="Dermatology Blog: Skin, Hair & Aesthetic Guides | DrPortals" description="Expert articles from verified dermatologists on acne, hair loss, anti-ageing, aesthetic procedures and skin care." />
      {/* Hero */}
      <section className="bg-[#23265d] text-white py-16 md:py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-64 h-64 bg-[#c5a065] rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-[#c5a065] mb-4">
            <BookOpen className="w-5 h-5" />
            <span className="font-medium">Knowledge Hub</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">
            {seo.h1 || 'Expert Dermatology Insights'}
          </h1>
          <p className="text-gray-300 max-w-2xl mb-8">
            Discover {totalCount}+ expert articles on skincare, hair care, and aesthetic treatments written by verified dermatologists.
          </p>
          
          {/* Category Pills */}
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => handleCategoryChange(cat.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === cat.key
                    ? 'bg-white text-[#23265d]'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
                data-testid={`category-${cat.key}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-2xl h-80 skeleton" />
            ))}
          </div>
        ) : blogs.length > 0 ? (
          <>
            {/* Featured Articles */}
            {page === 0 && featuredBlogs.length > 0 && (
              <div className="mb-12">
                <h2 className="font-heading text-2xl font-bold text-navy-900 mb-6">Featured Articles</h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {featuredBlogs.map((blog, index) => (
                    <Link
                      key={blog.id}
                      to={`/blog/${blog.slug}`}
                      className={`group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all ${
                        index === 0 ? 'md:col-span-2 md:row-span-2' : ''
                      }`}
                    >
                      <div className={`relative ${index === 0 ? 'h-full min-h-[400px]' : 'h-48'}`}>
                        <img
                          src={blog.featured_image || 'https://images.unsplash.com/photo-1655026392641-bf283a5f12d4?auto=format&fit=crop&w=1200&q=75'}
                          alt={blog.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 p-6">
                          <Badge className={`mb-3 ${getCategoryColor(blog.category)}`}>
                            {blog.category}
                          </Badge>
                          <h3 className={`font-heading font-semibold text-white mb-2 ${index === 0 ? 'text-2xl' : 'text-lg'}`}>
                            {blog.title}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-gray-300">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {blog.read_time} min read
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* All Articles Grid */}
            <div className="mb-8">
              <h2 className="font-heading text-2xl font-bold text-navy-900 mb-6">
                {page === 0 ? 'More Articles' : 'All Articles'}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {(page === 0 ? regularBlogs : blogs).map((blog) => (
                  <Link
                    key={blog.id}
                    to={`/blog/${blog.slug}`}
                    className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all"
                    data-testid={`blog-card-${blog.slug}`}
                  >
                    <div className="h-48 relative overflow-hidden">
                      <img
                        src={blog.featured_image || 'https://images.unsplash.com/photo-1655026392641-bf283a5f12d4?auto=format&fit=crop&w=1200&q=75'}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-5">
                      <Badge className={`mb-3 ${getCategoryColor(blog.category)}`}>
                        {blog.category}
                      </Badge>
                      <h3 className="font-heading font-semibold text-navy-900 mb-2 line-clamp-2 group-hover:text-[#23265d]/80">
                        {blog.title}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                        {blog.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {blog.read_time} min
                        </span>
                        <span className="flex items-center gap-1 text-[#23265d] font-medium group-hover:gap-2 transition-all">
                          Read More
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Pagination */}
            {totalCount > LIMIT && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  disabled={page === 0}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 text-sm text-gray-600">
                  Page {page + 1} of {Math.ceil(totalCount / LIMIT)}
                </span>
                <Button
                  variant="outline"
                  disabled={(page + 1) * LIMIT >= totalCount}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold text-gray-700 mb-2">
              No articles found
            </h3>
            <p className="text-gray-500">
              Check back soon for expert insights from our dermatologists.
            </p>
          </div>
        )}
      </div>
      <PageSeoContent seo={seo} />
    </div>
  );
};
